import React, { useState } from 'react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';
import { 
  FaUsers, 
  FaCalendarCheck, 
  FaEnvelope, 
  FaSmile, 
  FaArrowUp, 
  FaChartLine, 
  FaUserMd
} from 'react-icons/fa';

const DashboardStats = ({ submissions = [], appointments = [], users = [] }) => {
  const [timeframe, setTimeframe] = useState('6m');

  // Basic metric calculation
  const totalSubmissions = submissions.length;
  const totalAppointments = appointments.length;
  const totalUsers = users.length;
  
  // Confirmed ratio
  const confirmedAppts = appointments.filter(a => a.status === 'confirmed' || a.status === 'completed').length;
  const confirmationRate = totalAppointments > 0 ? Math.round((confirmedAppts / totalAppointments) * 100) : 100;

  // Format data for appointment status donut chart (strictly dynamic, no dummy fallbacks)
  const donutData = [
    { name: 'Confirmed', value: appointments.filter(a => a.status === 'confirmed' || a.status === 'completed').length },
    { name: 'Pending', value: appointments.filter(a => a.status === 'pending' || a.status === 'rescheduled').length },
    { name: 'Cancelled', value: appointments.filter(a => a.status === 'cancelled' || a.status === 'no-show').length },
  ].filter(item => item.value > 0);

  // Dynamic Satisfaction rate based on confirmed & completed vs cancelled
  const completedAppts = appointments.filter(a => a.status === 'completed' || a.status === 'confirmed').length;
  const cancelledAppts = appointments.filter(a => a.status === 'cancelled' || a.status === 'no-show').length;
  const totalCompletedOrCancelled = completedAppts + cancelledAppts;
  const satisfactionRate = totalCompletedOrCancelled > 0 ? Math.round((completedAppts / totalCompletedOrCancelled) * 100) : 100;

  // Helper to compile monthly timeline stats
  const getMonthlyData = () => {
    const monthlyData = {};
    const currentDate = new Date();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    // Initialize past 6 months
    const monthsLimit = timeframe === '3m' ? 3 : 6;
    for (let i = monthsLimit - 1; i >= 0; i--) {
      const month = new Date(currentDate);
      month.setMonth(currentDate.getMonth() - i);
      const monthKey = monthNames[month.getMonth()];
      monthlyData[monthKey] = { name: monthKey, submissions: 0, appointments: 0 };
    }
    
    // Count submissions by month
    submissions.forEach(submission => {
      if (!submission.date) return;
      const date = new Date(submission.date);
      const monthKey = monthNames[date.getMonth()];
      if (monthlyData[monthKey]) {
        monthlyData[monthKey].submissions += 1;
      }
    });
    
    // Count appointments by month
    appointments.forEach(appointment => {
      if (!appointment.date) return;
      const parts = appointment.date.split('-');
      if (parts.length < 2) return;
      const monthIndex = parseInt(parts[1]) - 1;
      const monthKey = monthNames[monthIndex];
      if (monthlyData[monthKey]) {
        monthlyData[monthKey].appointments += 1;
      }
    });
    
    return Object.values(monthlyData);
  };
  
  const chartData = getMonthlyData();
  
  // Custom Donut colors matching brand
  const DONUT_COLORS = ['#10b981', '#f59e0b', '#ef4444']; // Green, Amber, Red

  // Extract therapists dynamically from appointments to calculate caseload allocation (15 slots limit)
  const getTherapistCapacity = () => {
    const counts = {};
    appointments.forEach(appt => {
      const name = appt.therapist || 'Unassigned Clinician';
      if (!counts[name]) {
        counts[name] = { name, count: 0 };
      }
      counts[name].count += 1;
    });

    return Object.values(counts).map(t => {
      const maxSlots = 15;
      const usage = Math.min(Math.round((t.count / maxSlots) * 100), 100);
      return {
        name: t.name,
        role: t.name === 'Unassigned Clinician' ? 'Support Pool' : 'Clinician Staff Member',
        usage,
        color: usage > 80 ? 'bg-amber-500' : usage > 50 ? 'bg-purple-600' : 'bg-emerald-500'
      };
    }).sort((a, b) => b.usage - a.usage);
  };

  const therapistCapacity = getTherapistCapacity();

  return (
    <div className="space-y-6">
      {/* Title Header with timeframe filter */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white/70  backdrop-blur-md border border-slate-300  p-5 rounded-2xl shadow-md">
        <div>
          <h2 className="text-xl font-extrabold text-slate-800  tracking-tight flex items-center gap-2">
            <FaChartLine className="text-purple-600" /> Operational Analytics
          </h2>
          <p className="text-slate-400  text-xs">Real-time health CRM activities, intake rosters, and clinical indicators.</p>
        </div>
        <div className="flex items-center gap-1.5 bg-slate-100/80  p-1 rounded-xl border border-slate-200/50 ">
          <button 
            onClick={() => setTimeframe('3m')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${
              timeframe === '3m' ? 'bg-white  text-purple-700  shadow-md' : 'text-slate-500  hover:text-slate-800 :text-slate-200'
            }`}
          >
            3 Months
          </button>
          <button 
            onClick={() => setTimeframe('6m')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${
              timeframe === '6m' ? 'bg-white  text-purple-700  shadow-md' : 'text-slate-500  hover:text-slate-800 :text-slate-200'
            }`}
          >
            6 Months
          </button>
        </div>
      </div>

      {/* Summary Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1 */}
        <div className="relative overflow-hidden bg-white  border border-slate-300  p-5 rounded-2xl shadow-md hover-float group">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-purple-500/5 rounded-full group-hover:scale-125 transition-transform duration-500 pointer-events-none"></div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xxs font-bold text-slate-400  uppercase tracking-wider">Inquiries Received</span>
            <div className="p-2.5 bg-purple-50  text-purple-600  rounded-xl">
              <FaEnvelope size={15} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-800 ">{totalSubmissions}</span>
            <span className="text-emerald-600 text-xxs font-bold flex items-center gap-0.5">
              <FaArrowUp size={8} /> +12%
            </span>
          </div>
          <div className="mt-2 text-xxs text-slate-400  font-medium">Inbound contact form queries</div>
        </div>

        {/* Card 2 */}
        <div className="relative overflow-hidden bg-white  border border-slate-300  p-5 rounded-2xl shadow-md hover-float group">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-emerald-500/5 rounded-full group-hover:scale-125 transition-transform duration-500 pointer-events-none"></div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xxs font-bold text-slate-400  uppercase tracking-wider">Booked Sessions</span>
            <div className="p-2.5 bg-emerald-50  text-emerald-600  rounded-xl">
              <FaCalendarCheck size={15} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-800 ">{totalAppointments}</span>
            <span className="text-emerald-600 text-xxs font-bold flex items-center gap-0.5">
              <FaArrowUp size={8} /> +8%
            </span>
          </div>
          <div className="mt-2 text-xxs text-slate-400  font-medium">Scheduled patient slots</div>
        </div>

        {/* Card 3 */}
        <div className="relative overflow-hidden bg-white  border border-slate-300  p-5 rounded-2xl shadow-md hover-float group">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-blue-500/5 rounded-full group-hover:scale-125 transition-transform duration-500 pointer-events-none"></div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xxs font-bold text-slate-400  uppercase tracking-wider">Approved Accounts</span>
            <div className="p-2.5 bg-blue-50  text-blue-600  rounded-xl">
              <FaUsers size={15} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-800 ">{totalUsers}</span>
            <span className="text-slate-400  text-xxs font-bold">Total Directory</span>
          </div>
          <div className="mt-2 text-xxs text-slate-400  font-medium">Verified system members</div>
        </div>

        {/* Card 4 */}
        <div className="relative overflow-hidden bg-white  border border-slate-300  p-5 rounded-2xl shadow-md hover-float group">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-amber-500/5 rounded-full group-hover:scale-125 transition-transform duration-500 pointer-events-none"></div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xxs font-bold text-slate-400  uppercase tracking-wider">Satisfaction Rate</span>
            <div className="p-2.5 bg-amber-50  text-amber-600  rounded-xl">
              <FaSmile size={15} />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-slate-800 ">{satisfactionRate}%</span>
            <span className="text-emerald-600 text-xxs font-bold flex items-center gap-0.5">
              <FaArrowUp size={8} /> Computed
            </span>
          </div>
          <div className="mt-2 text-xxs text-slate-400  font-medium">Based on confirmed/completed ratio</div>
        </div>
      </div>

      {/* Visual Analytics Charts Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Large Gradient Area Chart */}
        <div className="lg:col-span-2 bg-white  border border-slate-300  p-5 rounded-2xl shadow-md flex flex-col justify-between">
          <div className="mb-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 ">Monthly Volume Streams</h3>
            <p className="text-slate-700  text-sm font-extrabold mt-0.5">Contact Inquiries vs Booked Appointments</p>
          </div>

          <div className="h-72 w-full mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="submissionsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9333ea" stopOpacity={0.25}/>
                    <stop offset="95%" stopColor="#9333ea" stopOpacity={0.0}/>
                  </linearGradient>
                  <linearGradient id="appointmentsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.25}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-neutral)" />
                <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fill: 'var(--text-secondary)', fontSize: 10, fontWeight: 600 }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--text-secondary)', fontSize: 10, fontWeight: 600 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'var(--bg-surface)', borderRadius: '12px', border: '1px solid var(--border-neutral)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', color: 'var(--text-primary)' }} 
                  labelStyle={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '12px' }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: 'bold', paddingTop: '10px' }} />
                <Area 
                  type="monotone" 
                  dataKey="submissions" 
                  name="Contact Queries" 
                  stroke="#9333ea" 
                  strokeWidth={2.5} 
                  fillOpacity={1} 
                  fill="url(#submissionsGrad)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="appointments" 
                  name="Patient Sessions" 
                  stroke="#10b981" 
                  strokeWidth={2.5} 
                  fillOpacity={1} 
                  fill="url(#appointmentsGrad)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Donut Distribution Chart */}
        <div className="bg-white  border border-slate-300  p-5 rounded-2xl shadow-md flex flex-col justify-between">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 ">Appointment Ratios</h3>
            <p className="text-slate-700  text-sm font-extrabold mt-0.5">Status Allocation Profile</p>
          </div>

          {donutData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-56 text-slate-400 py-10">
              <span className="text-xxs font-bold uppercase tracking-wider">No Scheduled Slots Found</span>
              <span className="text-slate-400 text-xs italic mt-1">Book sessions to view stats</span>
            </div>
          ) : (
            <>
              <div className="h-56 relative flex items-center justify-center my-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={85}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {donutData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'var(--bg-surface)', borderRadius: '8px', border: '1px solid var(--border-neutral)', fontSize: '11px', color: 'var(--text-primary)' }}
                      itemStyle={{ color: 'var(--text-primary)' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                
                {/* Centered Statistic inside Donut */}
                <div className="absolute text-center">
                  <span className="text-2xl font-black text-slate-800 ">{confirmationRate}%</span>
                  <span className="block text-xxs font-bold text-slate-400  uppercase tracking-wider">Approval</span>
                </div>
              </div>

              {/* Donut Legend Indicators */}
              <div className="grid grid-cols-3 gap-2 text-center text-xxs font-bold pt-3 border-t border-slate-50 ">
                {donutData.map((item, idx) => (
                  <div key={item.name} className="flex flex-col items-center">
                    <span className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: DONUT_COLORS[idx % DONUT_COLORS.length] }}></span>
                      <span className="text-slate-500 ">{item.name}</span>
                    </span>
                    <span className="text-slate-800  font-extrabold mt-0.5">{item.value} slots</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Roster Capacities & CRM Activity Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clinicians Resource Capacity */}
        <div className="bg-white  border border-slate-300  p-5 rounded-2xl shadow-md">
          <div className="flex items-center gap-2 border-b border-slate-300  pb-3 mb-4">
            <FaUserMd className="text-purple-600 " size={16} />
            <h3 className="font-extrabold text-slate-800  text-sm">Staff Capacity & Logs</h3>
          </div>

          <div className="space-y-4">
            {therapistCapacity.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400  text-xs">
                <span>No active clinician allocations recorded.</span>
              </div>
            ) : (
              therapistCapacity.map(tc => (
                <div key={tc.name} className="text-xs">
                  <div className="flex justify-between items-center mb-1">
                    <div>
                      <h4 className="font-bold text-slate-800 ">{tc.name}</h4>
                      <span className="text-xxs text-slate-400  font-medium">{tc.role}</span>
                    </div>
                    <span className="font-extrabold text-slate-700 ">{tc.usage}% Allocated</span>
                  </div>
                  {/* Visual Progress bar container */}
                  <div className="w-full bg-slate-100  h-2 rounded-full overflow-hidden">
                    <div 
                      className={`${tc.color} h-full rounded-full transition-all duration-1000`} 
                      style={{ width: `${tc.usage}%` }}
                    ></div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Operational Status Feed */}
        <div className="bg-white  border border-slate-300  p-5 rounded-2xl shadow-md">
          <div className="flex items-center gap-2 border-b border-slate-300  pb-3 mb-4">
            <FaUsers className="text-purple-600 " size={16} />
            <h3 className="font-extrabold text-slate-800  text-sm">System Operations Roster</h3>
          </div>

          <div className="space-y-3.5 text-xs">
            <div className="flex items-center justify-between p-2.5 bg-slate-50/70  border border-slate-300  rounded-xl">
              <div className="flex items-center gap-2.5">
                <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
                <span className="font-bold text-slate-800 ">Database API Connection</span>
              </div>
              <span className="text-xxs font-extrabold bg-emerald-50  text-emerald-700  px-2 py-0.5 rounded-full">Secure</span>
            </div>

            <div className="flex items-center justify-between p-2.5 bg-slate-50/70  border border-slate-300  rounded-xl">
              <div className="flex items-center gap-2.5">
                <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
                <span className="font-bold text-slate-800 ">HIPAA Auto-Masking Engine</span>
              </div>
              <span className="text-xxs font-extrabold bg-emerald-50  text-emerald-700  px-2 py-0.5 rounded-full">Active</span>
            </div>

            <div className="flex items-center justify-between p-2.5 bg-slate-50/70  border border-slate-300  rounded-xl">
              <div className="flex items-center gap-2.5">
                <span className="flex h-2.5 w-2.5 rounded-full bg-purple-500 animate-pulse"></span>
                <span className="font-bold text-slate-800 ">Live Client Notification Server</span>
              </div>
              <span className="text-xxs font-extrabold bg-purple-50  text-purple-700  px-2 py-0.5 rounded-full">Idle</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
